/**
 * useUserPresence.ts
 *
 * This hook provides real-time presence information (online/offline) of users in a given organization.
 *
 * ✅ Features:
 * - Fetches initial user presence data via a REST API.
 * - Subscribes to WebSocket events to keep presence data live and up-to-date.
 * - Stores presence data in a centralized Zustand store.
 *
 * 👤 Status Types:
 * - ONLINE
 * - OFFLINE
 */

import { useEffect } from 'react';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { useQuery } from 'react-query';
import {
  useUserPresenceStore,
  fetchUserPresence,
  OrgUserPresenceEvent,
  StatusTypes,
  UserPresenceResponse,
  UserStatusType,
} from '@frontend/userpresence';
import { useWebsocketEventSubscription } from '@frontend/websocket';

/**
 * Hook to get and maintain live user presence status in an organization.
 *
 * @param orgId - The organization ID for which to track user presence
 * @returns A list of users with their current online/offline status
 */

dayjs.extend(isSameOrBefore);

export const useUserPresenceWebsocketSubscription = (orgId: string) => {
  const { userPresenceData, setUserPresenceData, updateUserPresence, setLoading } = useUserPresenceStore(
    'userPresenceData',
    'setUserPresenceData',
    'updateUserPresence',
    'setLoading'
  );

  const { data, isLoading } = useQuery({
    queryKey: ['user-presence', orgId],
    queryFn: () => fetchUserPresence(orgId),
    enabled: !!orgId, // prevents query from running with undefined orgId and if userPresenceData is already there
    staleTime: 0,
  });

  // Set Zustand store when query data becomes available
  useEffect(() => {
    const typedData = data as UserPresenceResponse | undefined;
    if (typedData?.orgUserPresence) {
      setUserPresenceData(typedData.orgUserPresence);
    }
  }, [data]); // runs only when presence data changes

  // Sync isLoading with Zustand store
  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  // 2. Subscribe to WebSocket events to update presence in real time
  useWebsocketEventSubscription('ORG_USER_PRESENCE', (payload: OrgUserPresenceEvent) => {
    const { user_id, user_status, last_seen, org_id } = payload.params;
    // Validate and parse the last_seen date using day.js
    const lastSeenDate = dayjs(last_seen?.seconds * 1000);

    if (!lastSeenDate.isValid()) {
      console.error('Invalid date format for last_seen:', last_seen);
      return; // Exit if the date is invalid
    }

    if (org_id === orgId) {
      const status = user_status === 2 ? StatusTypes.ONLINE : StatusTypes.OFFLINE;
      const formattedLastSeenDate = lastSeenDate.toISOString(); // Format it as an ISO string
      updateUserPresence(user_id, status as UserStatusType, formattedLastSeenDate);
    }
  });

  return userPresenceData;
};
